import {
  TextInput as AdminTextInput,
  Confirm,
  Datagrid,
  DateField,
  DeleteWithConfirmButton,
  EditButton,
  Filter,
  FunctionField,
  List as RAList,
  ReferenceInput,
  TextArrayInput,
  TextField,
  downloadCSV,
  useDeleteMany,
  useGetIdentity,
  useListContext,
  useNotify,
  useRecordContext,
  useRefresh,
  useUnselectAll,
  useUpdate,
  useUpdateMany,
} from 'react-admin';

import { Button } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import CustomPagination from '../../CustomPagination';
import DateInput from '../../common/DateInput';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import SelectInput from '../../common/SelectInput';
import { UserRole } from '../../@generated/schemas';
import { convertEnumFilterItems } from '../../libs/convertEnumFilterItems';
import dayjs from 'dayjs';
import { getTokenSalt } from '../../libs/tokenHash';
import jsonExport from 'jsonexport/dist';
import { useState } from 'react';
import { useTranslate } from '../../locales';

export const UserFilter = (props: any) => {
  const { identity } = useGetIdentity();
  const translate = useTranslate();

  return (
    <Filter {...props}>
      <AdminTextInput
        label={translate('admin.search')}
        source="q"
        key="search"
        alwaysOn
      />
      <SelectInput
        choices={[
          { id: 0, name: 'Level 0' },
          { id: 1, name: 'Level 1' },
          { id: 2, name: 'Level 2' },
        ]}
        source="userLevel"
        label={translate('admin.userLevel')}
      />
      <AdminTextInput
        source="email"
        key="email"
        label={translate('admin.emailField')}
      />
      <AdminTextInput source="firstName" label={translate('admin.firstName')} />
      <AdminTextInput source="lastName" label={translate('admin.lastName')} />
      <TextArrayInput source="phone" label={translate('admin.phone')} />
      {[UserRole.SuperAdmin].includes(identity?.userRole) && (
        <ReferenceInput
          reference="Company"
          source="companyId"
          label={translate('admin.company')}>
          <SelectInput
            optionText="name"
            alwaysOn
            label={translate('admin.company')}
          />
        </ReferenceInput>
      )}
      <SelectInput
        source="userRole"
        label={translate('admin.userRole')}
        choices={convertEnumFilterItems(UserRole)}
      />
      <DateInput
        source="createdAt_gte"
        label={translate('admin.createdAtFrom')}
      />
      <DateInput
        source="createdAt_lte"
        label={translate('admin.createdAtTo')}
      />
    </Filter>
  );
};

const UnlockAccount = () => {
  const record = useRecordContext();
  const refreshData = useRefresh();
  const [update, { isLoading }] = useUpdate();
  const { identity } = useGetIdentity();
  const translate = useTranslate();

  const handleClick = async (data) => {
    await update('User', { id: record.id, data });
    refreshData();
  };

  if (!record.loginBlockedAt) {
    return (
      <Button
        variant="text"
        onClick={() =>
          handleClick({
            loginBlockedAt: new Date(),
            failedLoginAttempts: 0,
            tokenSalt: getTokenSalt(),
          })
        }
        color="primary"
        disabled={isLoading || identity?.id === record.id}
        startIcon={<LockIcon />}>
        {translate('admin.lock')}
      </Button>
    );
  }

  return (
    <Button
      variant="text"
      onClick={() =>
        handleClick({
          loginBlockedAt: {
            set: null,
          },
          failedLoginAttempts: 0,
        })
      }
      color="primary"
      disabled={isLoading || identity?.id === record.id}
      startIcon={<LockOpenIcon />}>
      {translate('admin.unlock')}
    </Button>
  );
};

const CustomDeleteButton = () => {
  const record = useRecordContext();
  const { identity } = useGetIdentity();

  if (identity?.id === record?.id) return null;

  return <DeleteWithConfirmButton mutationMode="pessimistic" record={record} />;
};

type BulkAction = 'lock' | 'unlock' | 'delete';

/**
 * Bulk lock / unlock / delete for the selected users. Every action goes
 * through the same per-user mutations as the row buttons (the data provider
 * fans UPDATE_MANY / DELETE_MANY out to updateUser / deleteUser), so locking
 * still rotates the token salt and deleting is still a soft delete. The
 * admin's own account is never selectable.
 */
export const UserBulkActionButtons = () => {
  const { selectedIds } = useListContext();
  const { identity } = useGetIdentity();
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const unselectAll = useUnselectAll('User');
  const [updateMany, { isLoading: isUpdating }] = useUpdateMany();
  const [deleteMany, { isLoading: isDeleting }] = useDeleteMany();
  const [pendingAction, setPendingAction] = useState<BulkAction | null>(null);

  const ids = (selectedIds || []).filter((id) => id !== identity?.id);
  const count = ids.length;
  const isLoading = isUpdating || isDeleting;

  const finish = (message: string) => {
    notify(message, { type: 'info', messageArgs: { count } });
    unselectAll();
    refresh();
    setPendingAction(null);
  };

  const fail = () => {
    notify('usersBulk.failed', { type: 'error' });
    refresh();
    setPendingAction(null);
  };

  const runUpdate = (data: Record<string, unknown>) =>
    updateMany(
      'User',
      { ids, data },
      {
        mutationMode: 'pessimistic',
        onSuccess: () => finish('usersBulk.updated'),
        onError: fail,
      },
    );

  const handleConfirm = () => {
    if (!count) {
      return setPendingAction(null);
    }

    if (pendingAction === 'lock') {
      return runUpdate({
        loginBlockedAt: new Date(),
        failedLoginAttempts: 0,
        tokenSalt: getTokenSalt(),
      });
    }

    if (pendingAction === 'unlock') {
      return runUpdate({
        loginBlockedAt: { set: null },
        failedLoginAttempts: 0,
      });
    }

    if (pendingAction === 'delete') {
      return deleteMany(
        'User',
        { ids },
        {
          mutationMode: 'pessimistic',
          onSuccess: () => finish('usersBulk.deleted'),
          onError: fail,
        },
      );
    }
  };

  return (
    <>
      <Button
        variant="text"
        color="primary"
        disabled={isLoading || !count}
        startIcon={<LockIcon />}
        onClick={() => setPendingAction('lock')}>
        {translate('usersBulk.lock')}
      </Button>
      <Button
        variant="text"
        color="primary"
        disabled={isLoading || !count}
        startIcon={<LockOpenIcon />}
        onClick={() => setPendingAction('unlock')}>
        {translate('usersBulk.unlock')}
      </Button>
      <Button
        variant="text"
        color="error"
        disabled={isLoading || !count}
        startIcon={<DeleteIcon />}
        onClick={() => setPendingAction('delete')}>
        {translate('usersBulk.delete')}
      </Button>
      {pendingAction && (
        <Confirm
          isOpen
          loading={isLoading}
          title={translate(`usersBulk.${pendingAction}ConfirmTitle`, { count })}
          content={translate(`usersBulk.${pendingAction}ConfirmContent`, {
            count,
          })}
          confirmColor={pendingAction === 'delete' ? 'warning' : 'primary'}
          onConfirm={handleConfirm}
          onClose={() => setPendingAction(null)}
        />
      )}
    </>
  );
};

const customExporter = (items) => {
  const listForExport = items.map((row) => {
    let userLevel = 0;
    if (row.userLevel.level < 100) {
      userLevel = 1;
    }
    if (row.userLevel.level === 100) {
      userLevel = 2;
    }
    return {
      'E-mail': row.email,
      'Company': row.company?.name,
      'First name': row.firstName,
      'Last name': row.lastName,
      'Address': `${row.address || ""} ${row.houseNumber || ""}`,
      'Postcode': `${row.postCode || ""}`,
      'City': `${row.city || ""}`,
      'Phone': row.phone,
      'User level': userLevel,
      'Created at': dayjs(row.createdAt).format('DD.MM. YYYY HH:mm'),
      'User role': row.userRole
    };
  });

  jsonExport(listForExport, { rowDelimiter: ';' }, (err, csv) =>
    downloadCSV('\uFEFF' + csv, 'User report'),
  );
};

export const UsersList = (props: any) => {
  const { identity } = useGetIdentity();
  const translate = useTranslate();
  const defaultFilter: any = {
    deletedAt: { equals: null },
  };

  if (identity?.userRole && identity?.userRole !== UserRole.SuperAdmin) {
    defaultFilter.company = {
      equals: identity.companyId,
    };
  }

  return (
    <RAList
      {...props}
      exporter={customExporter}
      filters={<UserFilter />}
      filter={defaultFilter}
      pagination={<CustomPagination />}>
      <Datagrid
        bulkActionButtons={<UserBulkActionButtons />}
        isRowSelectable={(record) => record?.id !== identity?.id}>
        <TextField source="email" label={translate('admin.emailField')} />
        <FunctionField
          label={translate('admin.company')}
          render={(row) => row?.company?.name || 'Drivemycar'}
        />
        <TextField source="firstName" label={translate('admin.firstName')} />
        <TextField source="lastName" label={translate('admin.lastName')} />
        <TextField source="phone" label={translate('admin.phone')} />
        <FunctionField
          label={translate('admin.userLevel')}
          render={(row) => {
            if (row.userLevel.level < 50) {
              return '0';
            }
            if (row.userLevel.level < 100) {
              return '1';
            }
            return (
              <div style={{ display: 'flex' }}>
                <span style={{ marginTop: 2, marginRight: 8 }}>2</span>{' '}
                <CheckIcon style={{ color: 'green' }} />
              </div>
            );
          }}
        />
        <DateField
          source="loginBlockedAt"
          label={translate('admin.loginBlocked')}
          locales="de-DE"
          showTime
        />
        <DateField
          source="createdAt"
          label={translate('admin.createdAt')}
          locales="de-DE"
          showTime
        />
        <TextField source="userRole" label={translate('admin.userRole')} />
        <EditButton />
        <UnlockAccount />
        <CustomDeleteButton />
      </Datagrid>
    </RAList>
  );
};
